import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { Link, useNavigate } from "react-router-dom";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import Select from "react-select";
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from "../staff/dropdownStyles";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import swal from "sweetalert";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";


import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import { getModernGridFilterPillSelectStyles } from "../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";

const DEPOSIT_SORT_FIELDS = {
  Priority: "Priority",
  RuleName: "RuleName",
  DepositValue: "DepositValue",
};

const DEPOSIT_SORT_LABELS = {
  [DEPOSIT_SORT_FIELDS.Priority]: "Priority",
  [DEPOSIT_SORT_FIELDS.RuleName]: "Rule Name",
  [DEPOSIT_SORT_FIELDS.DepositValue]: "Deposit Value",
};

const ManageAdvanceDeposit = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { getCompCurrency } = useProfileAndSettings();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [statusConfirmRule, setStatusConfirmRule] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const rulesFetchSeqRef = useRef(0);
  const compCurrency = getCompCurrency();
  const pageKey = "advanceDepositRules";
  const defaultVisibleColumns = {
    ruleName: true,
    depositType: true,
    depositValue: true,
    priority: false,
    isActive: true,
  };
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "RuleName", label: "Rule Name" };
  const defaultStatus = { value: "", label: "All" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];
  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(
    preferences.visibleColumns,
    defaultVisibleColumns
  );
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const status = preferences.status || defaultStatus;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();
  const [selectedItems, setSelectedItems] = useState(new Set());
  const lastSelectedIndex = useRef(null);

  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );
  const themeColor = useThemeColor();
  const SHOW_BULK_ACTIONS = false;

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const statusOptions = [
    { value: "", label: "All" },
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const statusSelectValue =
    statusOptions.find((o) => String(o.value) === String(status?.value ?? "")) ??
    defaultStatus;

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: DEPOSIT_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  // Check if any filter is applied
  const hasFiltersApplied = () => {
    return search.trim() !== "" || (status?.value !== "" && status?.value !== undefined);
  };

  const clearFilters = () => {
    setSearch("");
    dispatch(setFilterField({ pageKey, field: "status", value: defaultStatus }));
  };

  const navigate = useNavigate();

  useEffect(() => {
    // Check if preferences exist and merge with defaults
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      if (
        needsPreferencesUpdate(
          preferences.visibleColumns,
          defaultVisibleColumns
        )
      ) {
        const mergedColumns = mergeVisibleColumns(
          preferences.visibleColumns,
          defaultVisibleColumns
        );
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (!preferences.status) {
      dispatch(setFilterField({ pageKey, field: "status", value: defaultStatus }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line
  }, [dispatch, pageKey]);

  const fetchRules = useCallback(async () => {
    const fetchSeq = ++rulesFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ADVANCE_DEPOSIT_RULES, {
        params: {
          CompID,
          PageNumber: pageNumber,
          PageSize: parseInt(pageSize.value, 10),
          Search: search.trim() || null,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Status: status?.value !== "" && status?.value !== undefined ? status.value : null,
        },
      });

      if (fetchSeq !== rulesFetchSeqRef.current) return;

      const responseData = response.data.data || {};
      const rulesList = responseData.rules || responseData.Rules || [];
      const records = responseData.totalRecords || response.data.totalRecords || 0;
      const pages = responseData.totalPages || response.data.totalPages || 0;

      setRules(Array.isArray(rulesList) ? rulesList : []);
      setTotalRecords(records);
      setTotalPages(pages);

      if (!search.trim() && (status?.value === "" || status?.value === undefined)) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, records);
      }
    } catch (err) {
      if (fetchSeq !== rulesFetchSeqRef.current) return;
      setRules([]);
      setTotalRecords(0);
      setTotalPages(0);
      if (err.response?.status !== 404 && !search.trim()) {
        showErrorToast("Failed to load advance deposit rules.");
      }
    } finally {
      if (fetchSeq === rulesFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [search, sortBy, sortOrder, CompID, status, pageNumber, pageSize]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    status?.value,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(rulesFetchSeqRef, [
    search,
    status?.value,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchRules(), [fetchRules]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status?.value, sortBy?.value, sortOrder?.value, pageNumber, pageSize?.value]);

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    !hasFiltersApplied() &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No rules match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    if (status?.value !== "" && status?.value !== undefined) {
      return "No rules match the selected status. Try another status or clear filters.";
    }
    return "There are no advance deposit rules to show right now.";
  };

  const handleAddRule = () => navigate("/tableManagement/addAdvanceDeposit");

  const handleDeleteRule = async (ruleItem) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete rule "${ruleItem.ruleName}"?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      if (!ruleItem.id || !CompID) {
        showErrorToast("Invalid rule or company ID.");
        return;
      }

      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_ADVANCE_DEPOSIT_RULE, {
        params: {
          CompID,
          DepositRuleID: ruleItem.id,
        },
      });

      if (response.data.status) {
        setRules((prevRules) =>
          prevRules.filter((item) => item.id !== ruleItem.id)
        );
        showSuccessToast("Advance deposit rule deleted successfully.");
      } else {
        showErrorToast(response.data.message || "Deletion failed.");
      }
      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong while deleting the rule.");
      setLoading(false);
    }
  };

  const handleSelectItem = (ruleId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (rules[i]?.id) {
            newSelected.add(rules[i].id);
          }
        }
      } else {
        if (newSelected.has(ruleId)) {
          newSelected.delete(ruleId);
        } else {
          newSelected.add(ruleId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(rules.map((item) => item.id));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No rules selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected rules?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((ruleId) =>
          axiosInstance.delete(APIs.DELETE_ADVANCE_DEPOSIT_RULE, {
            params: {
              CompID,
              DepositRuleID: ruleId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchRules();
      showSuccessToast(`${selectedItems.size} rules deleted successfully.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected rules.");
      setLoading(false);
    }
  };

  const confirmRuleStatusChange = async () => {
    if (!statusConfirmRule) return;

    const { id, isActive, ruleName } = statusConfirmRule;
    const newStatus = !isActive;

    setRules((prev) =>
      prev.map((rule) =>
        rule.id === id ? { ...rule, isActive: newStatus } : rule
      )
    );
    setStatusConfirmRule(null);

    try {
      const response = await axiosInstance.post(
        APIs.POST_UPDATE_ADVANCE_DEPOSIT_RULE_STATUS,
        null,
        {
          params: {
            CompID,
            DepositRuleID: id,
            IsActive: newStatus,
          },
        }
      );

      if (response.data && response.data.status === false) {
        setRules((prev) =>
          prev.map((rule) =>
            rule.id === id ? { ...rule, isActive } : rule
          )
        );
        showErrorToast(response.data.message || "Failed to update rule status.");
        return;
      }
      showSuccessToast(
        `"${ruleName || "Rule"}" is now ${newStatus ? "Active" : "InActive"}.`
      );
    } catch (error) {
      setRules((prev) =>
        prev.map((rule) =>
          rule.id === id ? { ...rule, isActive } : rule
        )
      );
      showErrorToast(error.response?.data?.message || "Failed to update rule status.");
    }
  };

  const formatDepositValue = (rule) => {
    const symbol = getCurrencySymbol(compCurrency);
    switch (rule.depositType) {
      case "FixedAmount":
        return `${symbol}${rule.depositValue?.toFixed(2) || "0.00"}`;
      case "Percentage":
        return `${rule.depositValue || 0}%`;
      case "PerPerson":
        return `${symbol}${rule.depositValue?.toFixed(2) || "0.00"} per person`;
      default:
        return `${symbol}${rule.depositValue?.toFixed(2) || "0.00"}`;
    }
  };

  const getServiceAreasText = (rule) => {
    if (!rule.serviceAreaRelations || rule.serviceAreaRelations.length === 0) {
      return "All Areas";
    }
    return `${rule.serviceAreaRelations.length} Area(s)`;
  };

  const renderNoDataMessage = () => {
    if (loading || rules.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No rules found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <div>
      
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Advance Deposit Rules</li>
          </ol>
          <h1 className="page-header mb-0">Manage Advance Deposit Rules</h1>
        </div>
        <div className="ms-auto">
          <div className="d-flex justify-content-end align-items-center mb-3">
            <button
              className="btn px-4"
              onClick={handleAddRule}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: 'none',
                color: 'white',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`
              }}
              onMouseEnter={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
              Add Rule
            </button>
          </div>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && <AddButton onClick={handleAddRule} title="Add Rule" />}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Advance Deposit Rules"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by rule name or type..."
            bodyFlush
            topFilters={
              <ModernGridToolbarFilterPill
                iconClass="fa fa-toggle-on"
                className="modern-grid-toolbar__filter-pill--status"
              >
                <Select
                  value={statusSelectValue}
                  onChange={(val) =>
                    dispatch(setFilterField({ pageKey, field: "status", value: val }))
                  }
                  options={statusOptions}
                  styles={filterPillSelectStyles}
                  placeholder="All Status"
                  isSearchable={false}
                  {...defaultSelectPortalProps}
                />
              </ModernGridToolbarFilterPill>
            }
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
                defaultColumns={defaultVisibleColumns}
                pageKey={pageKey}
              />
            }
          >
            <div className="px-3 pb-3">
              {SHOW_BULK_ACTIONS && selectedItems.size > 0 && (
                <div
                  className="mb-3 p-3 bg-light rounded d-flex align-items-center justify-content-between"
                  style={{ border: "1px solid #dee2e6" }}
                >
                  <span className="fw-bold text-primary">
                    {selectedItems.size} rule{selectedItems.size > 1 ? "s" : ""} selected
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                      setSelectedItems(new Set());
                      lastSelectedIndex.current = null;
                    }}
                  >
                    Clear Selection
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={handleBulkDelete}
                  >
                    Delete
                  </button>
                </div>
              )}

              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {!loading && rules.length > 0 && (
                  <div
                    className="table-responsive table-container mt-3"
                    style={{
                      minHeight: "200px",
                      maxHeight: "600px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          {SHOW_BULK_ACTIONS && (
                            <th className="pt-2 pb-2">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={
                                  rules.length > 0 && selectedItems.size === rules.length
                                }
                              />
                            </th>
                          )}
                          {visibleColumns.ruleName && (
                            <SortableColumnHeader
                              sortField={DEPOSIT_SORT_FIELDS.RuleName}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              Rule Name
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.depositType && (
                            <th className="pt-2 pb-2">Deposit Type</th>
                          )}
                          {visibleColumns.depositValue && (
                            <SortableColumnHeader
                              sortField={DEPOSIT_SORT_FIELDS.DepositValue}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Deposit Value
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.priority && (
                            <SortableColumnHeader
                              sortField={DEPOSIT_SORT_FIELDS.Priority}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Priority
                            </SortableColumnHeader>
                          )}
                          <th className="pt-2 pb-2">Service Areas</th>
                          <th className="pt-2 pb-2">Date Range</th>
                          {visibleColumns.isActive && (
                            <th className="pt-2 pb-2 text-center">Status</th>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rules.map((rule, index) => (
                          <tr
                            key={rule.id}
                            onClick={(e) => {
                              if (
                                e.target.type === "checkbox" ||
                                e.target.closest(".dropdown") ||
                                e.target.closest(".status-badge")
                              ) {
                                return;
                              }
                              navigate(`/tableManagement/updateAdvanceDeposit/${rule.id}`);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            {SHOW_BULK_ACTIONS && (
                              <td className="align-middle" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={selectedItems.has(rule.id)}
                                  onChange={(e) => handleSelectItem(rule.id, index, e)}
                                />
                              </td>
                            )}
                            {visibleColumns.ruleName && (
                              <td className="align-middle">{rule.ruleName || "N/A"}</td>
                            )}
                            {visibleColumns.depositType && (
                              <td className="align-middle">{rule.depositType || "N/A"}</td>
                            )}
                            {visibleColumns.depositValue && (
                              <td className="align-middle text-center">
                                {formatDepositValue(rule)}
                              </td>
                            )}
                            {visibleColumns.priority && (
                              <td className="align-middle text-center">{rule.priority || 0}</td>
                            )}
                            <td className="align-middle">{getServiceAreasText(rule)}</td>
                            <td className="align-middle">
                              {rule.isSetDateRange
                                ? `${rule.depositStartDate || "N/A"} - ${rule.depositEndDate || "N/A"}`
                                : "No Date Range"}
                            </td>
                            {visibleColumns.isActive && (
                              <td
                                className="align-middle text-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <StatusBadge
                                  isActive={rule.isActive}
                                  onClick={() => setStatusConfirmRule(rule)}
                                />
                              </td>
                            )}
                            <td
                              className="align-middle text-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <TableActionsMenu>
                                <TableActionsItem
                                  as={Link}
                                  to={`/tableManagement/updateAdvanceDeposit/${rule.id}`}
                                  icon="fa fa-edit"
                                >
                                  Edit
                                </TableActionsItem>
                                <TableActionsItem
                                  danger
                                  icon="fa fa-trash"
                                  onClick={() => handleDeleteRule(rule)}
                                >
                                  Delete
                                </TableActionsItem>
                              </TableActionsMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

      <CustomModal
        title="Change rule status?"
        subtitle={
          statusConfirmRule
            ? `Are you sure you want to set "${statusConfirmRule.ruleName || "this rule"}" to ${
                statusConfirmRule.isActive ? "InActive" : "Active"
              }?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={() => setStatusConfirmRule(null)}
        isOpen={!!statusConfirmRule}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmRuleStatusChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />
    </div>
  );
};

export default ManageAdvanceDeposit;

